import React from "react";
import { Box, Inline, Stack, Text } from "braid-design-system";
import { BomIngredientRow, BomMenuItem } from "../../types/bom";
import ItemThumbnail from "./ItemThumbnail";

interface BomMappingViewProps {
    item: BomMenuItem;
    ingredients: BomIngredientRow[];
}

const Arrow: React.FC = () => (
    <Box display="flex" alignItems="center" justifyContent="center" style={{ minWidth: 32 }}>
        <Text tone="secondary" size="large">
            →
        </Text>
    </Box>
);

const MappingCard: React.FC<{
    imageUrl?: string | null;
    fallbackIcon?: string;
    title: string;
    subtitle: string;
    tone?: "brandAccentSoft" | "neutralLight";
}> = ({ imageUrl, fallbackIcon, title, subtitle, tone = "neutralLight" }) => (
    <Box background={tone} borderRadius="standard" padding="small" display="flex" alignItems="center" gap="small" style={{ minWidth: 180 }}>
        <ItemThumbnail imageUrl={imageUrl} alt={title} size={36} fontSize={20} fallbackIcon={fallbackIcon} />
        <Stack space="xxsmall">
            <Text weight="strong" size="small">
                {title}
            </Text>
            <Text tone="secondary" size="small">
                {subtitle}
            </Text>
        </Stack>
    </Box>
);

const BomMappingView: React.FC<BomMappingViewProps> = ({ item, ingredients }) => {
    return (
        <Box background="surface" borderRadius="standard" boxShadow="borderNeutralLight" padding="large">
            <Stack space="medium">
                <Inline space="xsmall" alignY="center">
                    <Text size="large">🔗</Text>
                    <Text weight="strong" size="large">
                        BOM Mapping View
                    </Text>
                </Inline>

                <Box display="flex" alignItems="center" style={{ flexWrap: "wrap", gap: 8 }}>
                    <MappingCard
                        imageUrl={item.image_url}
                        title={item.item_name}
                        subtitle={`(${item.item_code}) · ${item.yield_qty} ${item.uom}`}
                        tone="brandAccentSoft"
                    />
                    <Arrow />
                    <MappingCard fallbackIcon="📄" title="BOM" subtitle={`Version ${item.version} · Yield ${item.yield_qty}`} />
                    <Arrow />

                    {ingredients.length === 0 ? (
                        <Box padding="small">
                            <Text tone="secondary" size="small">
                                No ingredients mapped yet.
                            </Text>
                        </Box>
                    ) : (
                        <Inline space="small">
                            {ingredients.map((row) => (
                                <Box
                                    key={row.row_id}
                                    background="neutralLight"
                                    borderRadius="standard"
                                    padding="xsmall"
                                    display="flex"
                                    alignItems="center"
                                    style={{ minWidth: 130 }}
                                >
                                    <Text size="small">{row.ingredient_name}</Text>
                                </Box>
                            ))}
                        </Inline>
                    )}
                </Box>
            </Stack>
        </Box>
    );
};

export default BomMappingView;
